import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icon } from '../icons';
import { Popover } from './Overlay';
import { useFieldCtx } from './Input';

// Searchable single-select. Sync (options) or async (loadOptions with a query — debounced).
// Listbox pattern: aria-activedescendant, ↑↓ move, Enter picks, Esc closes, typing filters.

export interface ComboOption<V = string> {
  value: V;
  label: string;
  sub?: string;
  disabled?: boolean;
}

export interface ComboboxProps<V = string> {
  value: V | null;
  onChange: (value: V | null, option: ComboOption<V> | null) => void;
  options?: ComboOption<V>[];
  /** Async source: called with the typed query (debounced). */
  loadOptions?: (q: string) => Promise<ComboOption<V>[]>;
  /** Resolves the label for a value not present in `options` (edit forms). */
  resolveLabel?: (value: V) => Promise<string | null> | string | null;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  clearable?: boolean;
  size?: 'sm' | 'md';
  id?: string;
  autoFocus?: boolean;
  'aria-label'?: string;
  /** Render an extra option at the bottom (e.g. "Create …"). */
  footer?: (q: string, close: () => void) => ReactNode;
  emptyText?: string;
  debounceMs?: number;
}

export function Combobox<V = string>({
  value,
  onChange,
  options,
  loadOptions,
  resolveLabel,
  placeholder = 'Search…',
  disabled,
  invalid,
  clearable = true,
  size = 'md',
  id: idProp,
  autoFocus,
  footer,
  emptyText = 'No matches',
  debounceMs = 200,
  ...rest
}: ComboboxProps<V>) {
  const ctx = useFieldCtx();
  const autoId = useId();
  const id = idProp ?? ctx?.id ?? autoId;
  const listId = `${id}-list`;
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const [asyncOpts, setAsyncOpts] = useState<ComboOption<V>[]>([]);
  const [loading, setLoading] = useState(false);
  const [label, setLabel] = useState<string>('');

  // Selected label: from options, else resolve (async).
  useEffect(() => {
    if (value == null) {
      setLabel('');
      return;
    }
    const found = (options ?? []).concat(asyncOpts).find((o) => o.value === value);
    if (found) {
      setLabel(found.label);
      return;
    }
    if (resolveLabel) {
      let live = true;
      Promise.resolve(resolveLabel(value)).then((l) => live && setLabel(l ?? String(value)));
      return () => {
        live = false;
      };
    }
    setLabel(String(value));
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, options]);

  // Async loading, debounced on the query while open.
  useEffect(() => {
    if (!loadOptions || !open) return;
    let live = true;
    setLoading(true);
    const t = window.setTimeout(() => {
      loadOptions(q)
        .then((r) => live && setAsyncOpts(r))
        .catch(() => live && setAsyncOpts([]))
        .finally(() => live && setLoading(false));
    }, debounceMs);
    return () => {
      live = false;
      window.clearTimeout(t);
    };
  }, [q, open, loadOptions, debounceMs]);

  const items = useMemo(() => {
    if (loadOptions) return asyncOpts;
    const needle = q.trim().toLowerCase();
    const all = options ?? [];
    if (!needle) return all;
    return all.filter((o) => `${o.label} ${o.sub ?? ''}`.toLowerCase().includes(needle));
  }, [options, asyncOpts, q, loadOptions]);

  useEffect(() => setActive(0), [items.length, q]);

  const pick = (o: ComboOption<V>) => {
    if (o.disabled) return;
    onChange(o.value, o);
    setLabel(o.label);
    setQ('');
    setOpen(false);
    inputRef.current?.focus();
  };
  const clear = () => {
    onChange(null, null);
    setLabel('');
    setQ('');
    inputRef.current?.focus();
  };

  const shown = open ? q : label;
  const bad = invalid ?? ctx?.invalid ?? false;

  return (
    <div className="hf-combo" ref={wrapRef}>
      <div className={['hf-input-wrap', bad && 'invalid'].filter(Boolean).join(' ')} style={size === 'sm' ? { height: 26 } : undefined}>
        <span className="affix icon">
          <Icon name="search" size={13} />
        </span>
        <input
          ref={inputRef}
          id={id}
          role="combobox"
          className={['hf-input', size === 'sm' && 'sm'].filter(Boolean).join(' ')}
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && items[active] ? `${listId}-${active}` : undefined}
          aria-invalid={bad || undefined}
          aria-label={rest['aria-label']}
          autoComplete="off"
          placeholder={placeholder}
          value={shown}
          disabled={disabled ?? ctx?.disabled}
          autoFocus={autoFocus}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onChange={(e) => {
            setQ(e.target.value);
            if (!open) setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              if (!open) setOpen(true);
              else setActive((a) => Math.min(a + 1, items.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((a) => Math.max(a - 1, 0));
            } else if (e.key === 'Enter') {
              if (open && items[active]) {
                e.preventDefault();
                pick(items[active]!);
              }
            } else if (e.key === 'Backspace' && !q && value != null && clearable) {
              clear();
            } else if (e.key === 'Tab') {
              setOpen(false);
            }
          }}
        />
        {clearable && value != null && !disabled && (
          <button type="button" className="clear" aria-label="Clear" onClick={clear} tabIndex={-1}>
            <Icon name="x" size={13} />
          </button>
        )}
        {!(clearable && value != null) && (
          <span className="affix icon" style={{ padding: '0 8px 0 0' }}>
            <Icon name="chevron-down" size={13} />
          </span>
        )}
      </div>
      <Popover open={open} onClose={() => setOpen(false)} anchor={wrapRef} matchWidth role="presentation">
        <div id={listId} role="listbox" className="hf-listbox hf-scroll" aria-busy={loading || undefined}>
          {loading && items.length === 0 && <div className="hf-listbox-empty">Searching…</div>}
          {!loading && items.length === 0 && <div className="hf-listbox-empty">{emptyText}</div>}
          {items.map((o, i) => (
            <div
              key={String(o.value)}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              aria-disabled={o.disabled || undefined}
              className={['hf-option', o.value === value && 'chosen', o.disabled && 'hf-muted'].filter(Boolean).join(' ')}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(o)}
            >
              <span>{o.label}</span>
              {o.sub && <span className="sub">{o.sub}</span>}
            </div>
          ))}
          {footer?.(q, () => setOpen(false))}
        </div>
      </Popover>
    </div>
  );
}
